import { useCallback, useContext, type JSX } from 'react';
import useDebouncedEditorStateContext from '../contexts/useDebouncedEditorStateContext';
import { insertContent } from '../utils/documentUtils';
import { getGlobalVars } from '../utils/globalVarUtil';
import { convertToPlaintextState } from '../utils/plaintextUtils';
import { EditorConfigContext } from '../contexts/EditorConfigContext';

/** Configuration for the Macros capability (predefined text insertion) */
export type MacrosOptions = {
  /** Render function for the macros dropdown menu */
  renderMacrosMenu: (insertMacroText: (content: string) => void) => JSX.Element;
};

export default function MacrosMenu({ renderMacrosMenu }: MacrosOptions) {
  const { editorState, setEditorState } = useDebouncedEditorStateContext();
  const { usePlaintextMode, plaintextOptions } = getGlobalVars(
    useContext(EditorConfigContext)
  );

  const insertMacroText = useCallback(
    (content: string) => {
      const newState = insertContent({
        htmlBody: content,
        editorState,
        from: editorState.selection.from,
        to: editorState.selection.to,
      });

      setEditorState(
        usePlaintextMode
          ? convertToPlaintextState(newState, plaintextOptions)
          : newState
      );
    },
    [editorState, setEditorState, usePlaintextMode, plaintextOptions]
  );

  return renderMacrosMenu(insertMacroText);
}

MacrosMenu.displayName = 'MacrosMenu';
